/**
 * Rekenkern van de Alpina verzuimkostencalculator.
 *
 * Bewust UI-vrij (geen React-import) zodat de berekening los te testen is —
 * zie berekening.test.js. De React-component in calculator.jsx importeert
 * uitsluitend uit dit bestand; daar staan geen losse rekengetallen meer.
 */

/**
 * Loondoorbetaling volgens de Alpina-verdeling, per halfjaar:
 *   maand  1-6  → 100%
 *   maand  7-12 → 100%
 *   maand 13-18 →  70%
 *   maand 19-24 →  70%
 *
 * Elk halfjaar telt voor 0,5 jaar loonsom, dus de factor per blok is
 * percentage × 0,5. Jaar 1 = 1,00 · jaar 2 = 0,70 · twee jaar = 1,70.
 */
export const LOONDOORBETALING = Object.freeze({
	maand1Tot6: 0.5, // 100% × 0,5 jaar
	maand7Tot12: 0.5, // 100% × 0,5 jaar
	maand13Tot18: 0.35, // 70% × 0,5 jaar
	maand19Tot24: 0.35, // 70% × 0,5 jaar
});

/**
 * Totaalfactor over twee jaar (1,70). Afgeleid uit het schema hierboven zodat
 * er geen los magic number rondslingert; toFixed voorkomt drift van floats
 * (0,5 + 0,5 + 0,35 + 0,35 levert in IEEE-754 anders 1,7000000000000002 op).
 */
export const FACTOR_TWEE_JAAR = Number(
	Object.values(LOONDOORBETALING)
		.reduce((totaal, factor) => totaal + factor, 0)
		.toFixed(2)
);

/** Grenzen en stapgroottes van de invoervelden. */
export const MIN_LOONSOM = 0;
export const MAX_LOONSOM = 1000000;
export const STAP_LOONSOM = 5000;
export const DEFAULT_LOONSOM = 150000;

export const MIN_VERZUIM = 0.5;
export const MAX_VERZUIM = 15;
export const STAP_VERZUIM = 0.1;
export const DEFAULT_VERZUIM = 5.4;

/**
 * Standaardcijfers voor de keuzehulp. In Gutenberg zijn deze te bewerken
 * en uit te breiden; dit blijft de fallback voor tests en de harness.
 *
 * Een preset zonder `percentage` (null) wordt niet getoond.
 */
export const VERZUIM_PRESETS_CONFIG = Object.freeze([
	{ id: "landelijk", label: "Landelijk gemiddelde", percentage: 5.4, bron: "CBS", bronjaar: 2025 },
	{ id: "kantoorwerk", label: "Kantoorwerk", percentage: null, bron: "CBS", bronjaar: 2025 },
	{ id: "fysiek-werk", label: "Fysiek werk", percentage: null, bron: "CBS", bronjaar: 2025 },
	{ id: "zorg-welzijn", label: "Zorg en welzijn", percentage: 7.5, bron: "CBS", bronjaar: 2025 },
]);

/** Zet een Gutenberg- of configwaarde om naar een getal, of null als die ontbreekt. */
export function parsePresetPercentage(waarde) {
	if (waarde === null || waarde === undefined || waarde === "") return null;
	const getal = Number(String(waarde).trim().replace(",", "."));
	return Number.isFinite(getal) ? getal : null;
}

/** Presets die als knop getoond worden (label én percentage bekend). */
export function zichtbarePresets(lijst = VERZUIM_PRESETS_CONFIG) {
	return (Array.isArray(lijst) ? lijst : []).map((preset) => ({
		id: String(preset?.id || "").trim(),
		label: String(preset?.label || "").trim(),
		percentage: parsePresetPercentage(preset?.percentage),
	})).filter(
		(preset) => preset.id && preset.label && typeof preset.percentage === "number"
	);
}

export const VERZUIM_PRESETS = zichtbarePresets(VERZUIM_PRESETS_CONFIG);

/** Bronvermelding onder de presets, afgeleid uit de config. */
export const VERZUIM_PRESETS_BRON = `Gemiddelde verzuimpercentages op basis van ${
	VERZUIM_PRESETS_CONFIG[0]?.bron ?? "CBS"
}-jaarcijfers ${VERZUIM_PRESETS_CONFIG[0]?.bronjaar ?? ""}.`;

function clamp(waarde, min, max) {
	const getal = Number(waarde);
	if (!Number.isFinite(getal)) return min;
	return Math.min(Math.max(getal, min), max);
}

/**
 * Bereken de loondoorbetalingskosten bij verzuim over twee jaar.
 *
 *   basisJaarkosten      = jaarloonsom × (verzuimpercentage / 100)
 *   totaleKostenTweeJaar = basisJaarkosten × 1,70
 *
 * @param {number} loonsom           Bruto jaarloonsom in euro's.
 * @param {number} verzuimPercentage Verzuimpercentage (0-100).
 * @returns {{basisJaarkosten: number, maand1Tot6: number, maand7Tot12: number,
 *   maand13Tot18: number, maand19Tot24: number, totaleKostenTweeJaar: number}}
 *   Alle bedragen in hele euro's; basisJaarkosten onafgerond voor doorrekenen.
 */
export function berekenVerzuimkosten(loonsom, verzuimPercentage) {
	const veiligeLoonsom = clamp(loonsom, MIN_LOONSOM, MAX_LOONSOM);
	const veiligVerzuim = clamp(verzuimPercentage, 0, 100);
	const basisJaarkosten = veiligeLoonsom * (veiligVerzuim / 100);

	return {
		basisJaarkosten,
		maand1Tot6: Math.round(basisJaarkosten * LOONDOORBETALING.maand1Tot6),
		maand7Tot12: Math.round(basisJaarkosten * LOONDOORBETALING.maand7Tot12),
		maand13Tot18: Math.round(basisJaarkosten * LOONDOORBETALING.maand13Tot18),
		maand19Tot24: Math.round(basisJaarkosten * LOONDOORBETALING.maand19Tot24),
		totaleKostenTweeJaar: Math.round(basisJaarkosten * FACTOR_TWEE_JAAR),
	};
}

/** Standaard e-bike-cijfers voor de fiets-weergave (alleen als die optie aan staat). */
export const DEFAULT_FIETS_PRIJS = 3000;
export const DEFAULT_FIETS_MARGE_PERCENT = 30;

/** Winst per fiets: prijs × marge. €3.000 × 30% = €900. */
export function margePerFiets(
	prijs = DEFAULT_FIETS_PRIJS,
	margePercent = DEFAULT_FIETS_MARGE_PERCENT
) {
	const veiligePrijs = Number(prijs);
	const veiligeMarge = Number(margePercent);
	if (!Number.isFinite(veiligePrijs) || veiligePrijs <= 0) return 0;
	if (!Number.isFinite(veiligeMarge) || veiligeMarge <= 0) return 0;
	return veiligePrijs * (veiligeMarge / 100);
}

/** Aantal eenheden dat je moet verkopen om de kosten te dekken. */
export function aantalEenheden(kosten, eenheidswaarde) {
	const bedrag = Number(kosten) || 0;
	const waarde = Number(eenheidswaarde);
	if (!Number.isFinite(waarde) || waarde <= 0) return 0;
	return Math.round(bedrag / waarde);
}

/** Nederlandse valutanotatie zonder centen, bijv. "€ 13.260". */
export function formatEuro(bedrag) {
	return new Intl.NumberFormat("nl-NL", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 0,
	}).format(Math.round(Number(bedrag) || 0));
}

/** Nederlands getalformaat zonder valutateken, bijv. "150.000". */
export function formatGetal(getal) {
	return new Intl.NumberFormat("nl-NL").format(Number(getal) || 0);
}

/** Percentage in Nederlandse notatie met één decimaal, bijv. "5,4". */
export function formatPercentage(percentage) {
	return (Number(percentage) || 0).toFixed(1).replace(".", ",");
}
